import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE";
const ACCENT_DEEP = "#0891B2";

export default function CourseAccessPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const orderId = params.get("order_id");
  const [status, setStatus] = useState("checking"); // checking | paid | pending | error
  const [token, setToken] = useState(null);
  const [tries, setTries] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!orderId) {
      setStatus("error");
      setErr("Missing order_id");
      return;
    }
    let cancelled = false;

    async function check(attempt) {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/courses/signup/verify?order_id=${orderId}`);
        if (cancelled) return;
        if (res.data.status === "paid") {
          setToken(res.data.magic_token);
          setStatus("paid");
        } else if (attempt < 12) {
          setTries(attempt + 1);
          setTimeout(() => check(attempt + 1), 4000);
        } else {
          setStatus("pending");
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e?.response?.data?.detail || "Could not verify payment.");
        setStatus("error");
      }
    }
    check(0);
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex items-center justify-center p-4">
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-2xl p-8 max-w-md w-full text-center" data-testid="access-page">
        {status === "checking" && (
          <>
            <Loader2 size={36} className="animate-spin mx-auto text-cyan-400 mb-4" />
            <p className="text-white text-lg font-bold mb-1">Confirming your payment…</p>
            <p className="text-zinc-400 text-sm">This usually takes 5–15 seconds. Don't close this page. (attempt {tries + 1}/13)</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={48} className="mx-auto text-emerald-400 mb-3" />
            <p className="text-white text-xl font-bold mb-1">You're in! 🎉</p>
            <p className="text-zinc-400 text-sm mb-5">We've also emailed your access link.</p>
            <button
              onClick={() => navigate(`/learn/${slug}?token=${token}`)}
              className="inline-flex items-center justify-center gap-1.5 px-5 py-3 rounded-lg text-black font-bold text-sm"
              style={{ background: `linear-gradient(90deg, ${ACCENT}, ${ACCENT_DEEP})` }}
              data-testid="start-learning-btn"
            >
              Start Learning <ArrowRight size={14} />
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <AlertCircle size={36} className="mx-auto text-amber-400 mb-3" />
            <p className="text-white text-lg font-bold mb-1">Payment still processing…</p>
            <p className="text-zinc-400 text-sm mb-5">Your bank hasn't confirmed yet. We'll auto-email your access link once it clears. You can also refresh this page.</p>
            <button onClick={() => window.location.reload()} className="text-cyan-300 text-sm hover:underline">Refresh now</button>
          </>
        )}
        {status === "error" && (
          <>
            <AlertCircle size={36} className="mx-auto text-red-400 mb-3" />
            <p className="text-white text-lg font-bold mb-1">Verification failed</p>
            <p className="text-zinc-400 text-sm mb-5">{err}</p>
            <Link to={`/courses/${slug}`} className="text-cyan-300 text-sm hover:underline">Back to course</Link>
          </>
        )}
      </div>
    </div>
  );
}
